import React, { useCallback, useEffect, useMemo } from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import { intl } from '../../../../lib';
import type { LanguageProperty } from '../../../generated/LanguageProperty';
import type { Component } from '@formio/react';
import { Radio, RadioGroup } from '../../../form';
import { dataGridHelperComponent } from '../helpers/dataGrid';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioRadioConstructor = {
  classNames: string;
  classNamesLabel: string;
  classNamesRadios: string;
  radioOptions: Array<RadioOption>;
};
/**
 * @param classNames a string separated by spaces that will be added as classNames
 * @param radioOptions the options the user will be able to select
 */
export type EhsFormioRadioSettings = {
  classNames?: string;
  classNamesLabel?: string;
  classNamesRadios?: string;
  radioOptions: Array<RadioOption>;
};
export const EhsFormioRadioType = 'ehsRadio';
export class EhsFormioRadiobox extends EhsFormioReactComponent<
  EhsFormioRadioSettings,
  EhsFormioRadioConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioRadioType,
      label: 'common.unknown',
      key: EhsFormioRadioType,
      classNames: '',
      classNamesLabel: '',
      classNamesRadios: '',
      radioOptions: [],
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-Radiobox',
      group: 'advanced',
      icon: 'dot-circle-o',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioRadiobox.schema()
    };
  }
  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'classNames',
          label: 'classNames',
          tooltip:
            ' A string separated by spaces that will be added as classNames',
          weight: 20
        },
        {
          type: 'textfield',
          key: 'classNamesLabel',
          label: 'classNamesLabel',
          tooltip:
            ' A string separated by spaces that will be added as classNames for the label',
          weight: 21
        },
        {
          type: 'textfield',
          key: 'classNamesRadios',
          label: 'classNamesRadios',
          tooltip:
            ' A string separated by spaces that will be added as classNames for the radio boxes',
          weight: 22
        },
        dataGridHelperComponent(
          {
            label: 'radioOptions',
            tooltip: 'The options that can be selected (each Radiobox)',
            key: 'radioOptions',
            weight: 30
          },
          [
            {
              type: 'textfield',
              key: 'radioLabel',
              label: 'radioLabel'
            },
            {
              type: 'textfield',
              key: 'radioValue',
              label: 'radioValue'
            },
            {
              type: 'checkbox',
              key: 'default',
              label: 'Default'
            }
          ]
        )
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  private classNamesLabel = '';
  private classNamesRadios = '';
  private radioOptions = [{ radioLabel: '', radioValue: '', default: false }];
  extractFields(component: Component<EhsFormioRadioConstructor>): void {
    this.classNames = component.classNames;
    this.classNamesLabel = component.classNamesLabel;
    this.classNamesRadios = component.classNamesRadios;
    this.radioOptions = component.radioOptions;
    this.shouldSetValue = true;
    this.dataForSetting = this._data[component.key as string];
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioRadioSettings>) => JSX.Element,
    EhsFormioRadioSettings
  ] {
    return [
      RadioboxCmp,
      {
        classNames: this.classNames,
        classNamesLabel: this.classNamesLabel,
        classNamesRadios: this.classNamesRadios,
        radioOptions: this.radioOptions
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

type RadioOption = {
  radioLabel: string;
  radioValue: string;
  default: boolean;
};

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link EhsFormioRadioSettings} properties
 */
function RadioboxCmp({
  value,
  onChange,
  label,
  readOnly,
  required,
  settings,
  keyForComponent
}: Readonly<EhsFormioReactProps<EhsFormioRadioSettings>>): JSX.Element {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );
  if (!value) {
    const defaultValue = settings.radioOptions.find(option => option.default);
    if (defaultValue?.radioValue) {
      value = defaultValue.radioValue;
    }
  }
  const onChangeWrapper = useCallback(
    (val): void => {
      onChange(val);
    },
    [onChange]
  );
  useEffect(() => {
    // We need to change the default value on first render, otherwise formio cannot properly validate with default and required
    const defaultValue = settings.radioOptions.find(option => option.default);
    if (defaultValue) {
      onChangeWrapper(defaultValue.radioValue);
    }
  }, [onChangeWrapper, settings.radioOptions]);

  const wrapperValue = useMemo(() => {
    return value ?? undefined;
  }, [value]);
  const radiogroup = useMemo(() => {
    const classNames = () => {
      if (settings.classNamesRadios) {
        return settings.classNamesRadios;
      } else {
        return 'ehs-grid grid-xs-11';
      }
    };
    return (
      <RadioGroup
        value={wrapperValue}
        onChange={onChangeWrapper}
        readOnly={readOnly}
        className={classNames()}
        row={!isQuestionnaire}
        label={label}
        required={required}
        error={errorMessage}
        labelOnTop={isQuestionnaire}
      >
        {settings.radioOptions.map((radio: RadioOption) => {
          return (
            <Radio
              key={radio.radioValue}
              label={intl(
                radio.radioLabel as LanguageProperty,
                radio.radioLabel
              )}
              value={radio.radioValue}
              readOnly={readOnly}
            />
          );
        })}
      </RadioGroup>
    );
  }, [
    wrapperValue,
    onChangeWrapper,
    readOnly,
    label,
    required,
    errorMessage,
    isQuestionnaire,
    settings.radioOptions,
    settings.classNamesRadios
  ]);

  return (
    <div className={settings.classNames ?? '' + 'ehs-grid container'}>
      {radiogroup}
    </div>
  );
}
